/**  React libraries */
import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from 'react-bootstrap';
import { useDispatch, useSelector } from "react-redux";
import { Col, FormControl, Nav, Row, Table } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";

/**  Custom components stores services types constant */
import { AppDispatch, RootState } from "../../store/store";
import { LogoRequest, LogoRequesListConstants, SortableKeys } from "./logoRequestList.types";
import { fetchLogoRequests } from "./logoRequestSlice";
import Toaster from "../../components/Toaster/Toster";
import { CERTIFICATE_STATUS, CertificateData } from "../Certificates/certificates.types";
import { fetchCertificates } from "../Certificates/certificatesSlice";
import Loader from "../../components/Loader/Loader";
import { dateConversion } from "../../utils/dateHandler";
import ErrorMessage from "../../components/Error-Message/Error-Message";
import { getStatusLabel, getStatusStyles, STATUS } from "../../utils/statusHandler";
import { logoRequestService } from "../LogoRequest/logoRequestService";
import { ProductType, ProductTypeString } from "../LogoRequest/logoRequest.Type";
import SearchInput from "./../../components/SearchInput/SearchInput";
import { createRequest } from "./../LogoRequestList/logoRequestSlice";
import { DEFAULT_ERROR_MSG } from "../../utils/constants";

// Images
import calendarIcon from "../../assets/images/calendarIcon.svg";
import plusIcon from "../../assets/images/plusIcon.svg";
import searchIcon from "../../assets/images/searchicon.svg";
import onProductLogo from "../../assets/images/onProductLogo.svg";
import offProductLogo from "../../assets/images/offProductLogo.svg";


/** Styles */
import "react-datepicker/dist/react-datepicker.css";
import "./logoRequest.style.css";

const LogoRequestList: React.FC = () => {
  const { logoRequestList, logoRequestListloading, logoRequestError, requestListCertificateId } = useSelector((state: RootState) => state.logorequestList);
  const { certificates, certificatesLoading, certificatesError } = useSelector((state: RootState) => state.certificates);
  const dispatch = useDispatch<AppDispatch>();
  const { certificateId } = useParams<{ certificateId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("All requests");
  const [searchValue, setSearchValue] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const location = useLocation();
  const [showToaster, setTosterShow] = useState(false);
  const [toasterInfo, setToasterInfo] = useState<{ type: "success" | "error" | "warning", message: string }>({ type: "success", message: "" });
  const [currentCertificate, setCurrentCertificate] = useState<CertificateData>({
    id: "",
    accountName: "",
    certificateId: "",
    validTo: "",
    status: ""
  });
  const userState = useSelector((state: RootState) => state.users);
  const [loadingCreateLogoRequest, setLoadingCreateLogoRequest] = useState(false);
  const [errorCreateLogoRequest, setErrorCreateLogoRequest] = useState<string>('');

  const [sortConfig, setSortConfig] = useState<{
    key: SortableKeys | null;
    direction: "asc" | "desc";
  }>({
    key: "convertedSubmittedDate",
    direction: "desc",
  });

  useEffect(() => {
    if (certificates.length > 0) {
      const data = certificates?.find(certificate => certificate.id === certificateId);
      if (data) {
        setCurrentCertificate(data)
      }
    } else {
      const loadCertificates = async () => {
        const resultAction = await dispatch(fetchCertificates());
        if (fetchCertificates.fulfilled.match(resultAction)) {
          const certificates = resultAction.payload;
          const data = certificates?.find(certificate => certificate.id === certificateId);
          if (data) {
            setCurrentCertificate(data)
          }
        } else {
          console.error("Error fetching Certificates:", resultAction.error.message);
        }
      };
      // Load certificates
      loadCertificates();
    }

    if (requestListCertificateId != certificateId) {
      let requestId: string = certificateId ?? "";
      dispatch(fetchLogoRequests(requestId));
    }

    // Uncomment below code when we need to fetach data from backend every time
    // let requestId: string = certificateId ?? "";
    //dispatch(fetchLogoRequests(requestId));
  }, []);


  /**
  * Get logo request details
  * @param logoRequestId 
  */
  const createLogoRequest = async (requestType: ProductType, certificateId: string, licenseCodeLabelingAgreement: string, fscAgreementPath: string) => {
    try {
      setLoadingCreateLogoRequest(true);
      const requestDetailResponse = await logoRequestService.createLogoRequest(requestType, certificateId, licenseCodeLabelingAgreement, fscAgreementPath);
      let logoRequestId = requestDetailResponse.requestId;
      let siteNames = "";
      if (requestDetailResponse.requestDetailsData.siteName.length > 0) {
        siteNames = requestDetailResponse.requestDetailsData.siteName.join(", ");
      }
      dispatch(createRequest({
        requestId: logoRequestId,
        requestName: requestDetailResponse.requestName,
        requestType: requestDetailResponse.requestType,
        submittedDate: "",
        status: STATUS.DRAFT,
        jobName: "",
        jobId: "",
        siteName: siteNames,
        convertedSubmittedDate: "",
        displayStatus: getStatusLabel(STATUS.DRAFT)
      }));

      navigate(`/logoRequest/${certificateId}/${logoRequestId}/${requestType}`, { state: { responseData: requestDetailResponse } });
    } catch (err: any) {
      setErrorCreateLogoRequest(err.message);
      setTosterShow(true);
      let eMessage = (err.message) ? err.message : "Error in creating logo request";
      setToasterInfo({ type: "error", message: eMessage });
    } finally {
      setLoadingCreateLogoRequest(false);
    }
  }

  /**
   * Handle tab click
   * @param tab 
   */
  const handleTabClick = (tab: string) => {
    try {
      setCurrentPage(1);
      setActiveTab(tab);
    } catch (exe) {
      console.log(exe);
    }
  };

  /**
   * Handle date change
   * @param dates 
   */
  const handleDateChange = (dates: [Date | null, Date | null]) => {
    try {
      const [start, end] = dates;
      if (!start && !end) {
        setStartDate(undefined);
        setEndDate(undefined);
      } else {
        setStartDate(start || undefined);
        setEndDate(end || undefined);
      }
    } catch (exe) {
      console.log(exe);
    }
  };

  /**
   * Format Date
   * @param date 
   * @returns 
   */
  const formatDate = (date: Date | undefined): string => {
    try {
      if (!date) return "";
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}. ${month}. ${year}`;
    } catch (exe) {
      console.log(exe);
      return " ";
    }
  };

  /**
   * Format date range
   */
  const formattedDateRange =
    startDate && endDate
      ? `(${formatDate(startDate)} - ${formatDate(endDate)})`
      : startDate
        ? `(${formatDate(startDate)} - )`
        : "";

  /**
   * Filter by date
   * @param logoRequestItem 
   * @returns 
   */
  const filterByDate = (logoRequestItem: LogoRequest) => {
    try {
      const submittedDate = new Date(logoRequestItem.submittedDate);

      if (!startDate && !endDate) return true;

      if (startDate && endDate) {
        return submittedDate >= startDate && submittedDate <= endDate;
      }
      if (startDate) {
        return submittedDate >= startDate;
      }
      if (endDate) {
        return submittedDate <= endDate;
      }
      return true;
    } catch (exe) {
      console.log(exe);
      return false;
    }
  };

  /**
   *  Filter logo request as per tabs search and date range
   * @returns 
   */
  const filteredLogoRequestData = useMemo<LogoRequest[]>(() => {
    try {
      const search = searchValue.toLowerCase();

      // 1️⃣ Apply filters (tabs, search, date)
      const filtered = logoRequestList
        .filter((row) => {
          if (activeTab === "All requests") return true;
          return row.displayStatus === activeTab;
        })
        .filter((row) => {
          return (
            row.requestName?.toLowerCase().includes(search) ||
            row.requestType?.toLowerCase().includes(search) ||
            row.jobId?.toLowerCase().includes(search) ||
            row.jobName?.toLowerCase().includes(search) ||
            row.siteName?.toLowerCase().includes(search) ||
            row.status?.toLowerCase().includes(search) ||
            row.convertedSubmittedDate?.toLowerCase().includes(search)
          );
        })
        .filter(filterByDate);

      // 2️⃣ Sorting (ONLY after filtering)
      if (!sortConfig.key) return filtered;

      const sorted = [...filtered].sort((a, b) => {
        const key = sortConfig.key!;
        
        if (key === "requestName") {
          const numA = Number(a.requestName.replace(/\D/g, ""));
          const numB = Number(b.requestName.replace(/\D/g, ""));
          return numA - numB;
        }

        if (key === "convertedSubmittedDate") {
          const dateA = a[key] ? new Date(a[key]).getTime() : 0;
          const dateB = b[key] ? new Date(b[key]).getTime() : 0;
          return dateA - dateB;
        }

        return a[key].localeCompare(b[key]);
      });

      return sortConfig.direction === "asc" ? sorted : sorted.reverse();

    } catch (exe) {
      console.error("Error filtering logo requests:", exe);
      return [];
    }
  }, [activeTab, searchValue, logoRequestList, sortConfig]);

  const totalPages = Math.ceil(filteredLogoRequestData.length / LogoRequesListConstants.DATA_PER_PAGE);
  const startIdx = (currentPage - 1) * LogoRequesListConstants.DATA_PER_PAGE;
  const endIdx = Math.min(startIdx + LogoRequesListConstants.DATA_PER_PAGE, filteredLogoRequestData.length);
  const logoRequestPageData = filteredLogoRequestData.slice(startIdx, endIdx);

  /**
   * Navigate to Pagination page
   * @param page 
   * @returns 
   */
  const goToPage = (page: number) => setCurrentPage(page);

  /**
  * On Seach field key up
  * @param searchText 
  * @returns 
  */
  const onSeachFieldKeyUp = (searchText: string) => {
    setCurrentPage(1);
    setSearchValue(searchText);
  }

  /**
   * On Add Request CLick
   * @param eventKey :- type of request
   */
  const onAddRequestClick = (eventKey: string | null) => {
    //navigate(`/logoRequest/${certificateId}/${eventKey}`);
    let requestType: ProductType = ProductType.OnProduct;
    if (eventKey === '2') {
      requestType = ProductType.OffProduct;
    }
    let cId = (certificateId) ? certificateId : '';
    createLogoRequest(requestType, cId, "", "");
  };

  /**
   * On Click on logo request row item
   * @param logoRequestItem :- Logo request item
   */
  const onLogoRequestItemClick = (logoRequestItem: LogoRequest) => {
    try {
      let reqType = ProductType.OffProduct;
      if (logoRequestItem.requestType == ProductTypeString.OnProduct) {
        reqType = ProductType.OnProduct;
      }
      navigate(`/logoRequest/${certificateId}/${logoRequestItem.requestId}/${reqType}`);
    } catch (exe) {
      console.log(exe);
    }
  }

  /**
   * column heder width
   * @param coumnName 
   * @returns 
   */
  const columnHeaderWidth = (coumnName: string) => {
    let width = undefined;
    if (coumnName === 'Site Name') {
      width = "200px";
    } else if (coumnName === 'Status') {
      width = "130px";
    }
    return width;
  }

  // Display loader while fetching data 
  if (certificatesLoading || logoRequestListloading) {
    return <Loader />;
  }

  // Display Error while fetching data 
  if (certificatesError || logoRequestError) {
    const errorMessage = certificatesError || logoRequestError || DEFAULT_ERROR_MSG;
    return <ErrorMessage message={errorMessage} />
  }

  const handleSort = (key: SortableKeys | null) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  return (
    <>
      <Row className="py-2">
        <Col className="d-flex justify-content-between align-items-center">
          {loadingCreateLogoRequest && <div className="loader-overlay"><Loader /></div>}
          <div className="username-title"> Logo Request of : <span>{currentCertificate.accountName}</span> / <span>{currentCertificate.certificateId}</span></div>
          {currentCertificate.status === CERTIFICATE_STATUS.CERTIFIED &&
            <Dropdown onSelect={onAddRequestClick}>
              <Dropdown.Toggle variant="primary" id="dropdown-basic" className="d-flex align-items-center rounded-pill">
                <img src={plusIcon} alt="New Request Icon" className="me-2" width="24" height="24" />
                <div>New Request</div>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item eventKey="1" className="d-flex align-items-center"><img
                  src={onProductLogo}
                  alt="On-Product Logo Request"
                  width="24"
                  height="24"
                  className="me-2"
                />
                  <span>On-Product Logo Request</span></Dropdown.Item>
                <Dropdown.Item eventKey="2" className="d-flex align-items-center"><img
                  src={offProductLogo}
                  alt="Off-Product Logo Request"
                  width="24"
                  height="24"
                  className="me-2"
                />
                  <span>Off-Product Logo Request</span></Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          }
        </Col>
      </Row>
      <hr className="mt-3 mb-4"></hr>
      <Row className="bg-white py-3 px-4 m-1">
        <Col xl={9} className="mb-lg-3 mb-xl-0">
          <Nav defaultActiveKey="All requests">
            {LogoRequesListConstants.FILTERTABS.map((tab: any) => (
              <Nav.Item key={tab}>
                <Nav.Link
                  eventKey={tab}
                  onClick={() => handleTabClick(tab)}
                  className={activeTab === tab ? "active-tab" : "inactive-tab"}
                >
                  {tab}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
        <Col xl={3}>
          <div className="d-flex flex-column flex-md-row justify-content-md-end justify-content-start gap-3">
            <SearchInput value={searchValue} onChange={setSearchValue} placeholder="Search logo request" />
            {/*
                 <div className="date-picker-container">
                    <DatePicker
                      selected={startDate}
                      onChange={handleDateChange}
                      selectsRange
                      startDate={startDate}
                      endDate={endDate}
                      className="form-control custom-datepicker"
                      placeholderText="Select Date"
                      dateFormat="MM/dd/yyyy"
                      value={formattedDateRange}
                    />
                    <img
                      src={calendarIcon}
                      alt="Calendar Icon"
                      className="position-absolute "
                      style={{
                        width: "15px",
                        height: "16.67px",
                        transform: "translateY(-63%)",
                        right: "0px",
                        marginRight: "5px",
                        top: "53%",
                      }}
                    />
                  </div>
              */}
          </div>
        </Col>
        <div className="table-responsive">
          <Table hover className="text-center align-middle">
            <colgroup>
              <col style={{ width: "10%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead>
              <tr>
                {LogoRequesListConstants.LOGO_REQUEST_LIST_HEADERS.map((col, index) => (
                  <th
                    key={index}
                    className="request-queue-table-header"
                    style={{
                      width: columnHeaderWidth(col.label),
                      cursor: col.sortKey ? "pointer" : "default",
                    }}
                    onClick={() => col.sortKey && handleSort(col.sortKey)}
                  >
                    <span className="th-container d-inline-flex">
                      {col.label}

                      {col.sortKey && (
                        <span className="sort-arrows">
                          <span
                            className={
                              "sort-arrow " +
                              (sortConfig.key === col.sortKey &&
                                sortConfig.direction === "asc"
                                ? "active"
                                : "")
                            }
                          >
                            ▲
                          </span>
                          <span
                            className={
                              "sort-arrow " +
                              (sortConfig.key === col.sortKey &&
                                sortConfig.direction === "desc"
                                ? "active"
                                : "")
                            }
                          >
                            ▼
                          </span>
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logoRequestPageData.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center" }}>
                    {activeTab === "All requests" ? (
                      "Please create a FSC logo request to get started."
                    ) : (
                      "No Data Found"
                    )}
                  </td>
                </tr>
              ) : (
                logoRequestPageData.map((item, index) => (
                  <tr style={{ cursor: 'pointer' }} key={index} onClick={() => onLogoRequestItemClick(item)}>
                    <td style={{ fontSize: "12px", color: "#353535" }}>
                      {item.requestName}
                    </td>
                    <td style={{ fontSize: "12px", color: "#353535" }}>
                      {item.requestType}
                    </td>
                    <td style={{ fontSize: "12px", color: "#353535" }}>
                      {item.convertedSubmittedDate}
                    </td>
                    <td
                      style={{
                        fontSize: "12px",
                        color: "#353535"
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-start">
                        <div
                          className="rounded-circle mr-2 status-badge"
                          style={{
                            backgroundColor: getStatusStyles(item.status)
                              .backgroundColor,
                            height: "10px",
                            textAlign: "start",
                          }}
                        />
                        <p className="text-start mb-0">{item.displayStatus}</p>
                      </div>
                    </td>

                    <td style={{ fontSize: "12px", color: "#353535" }}>
                      {item.jobName}
                    </td>
                    <td style={{ fontSize: "12px", color: "#353535" }}>
                      {item.jobId}
                    </td>
                    <td style={{ fontSize: "12px", color: "#353535" }}>
                      {item.siteName}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
        {filteredLogoRequestData.length > LogoRequesListConstants.DATA_PER_PAGE && (
          <div className="d-flex align-items-center justify-content-center">
            <p className="mb-0 text-secondary">
              Showing {startIdx + 1} to {endIdx} of {filteredLogoRequestData.length}{" "}
              entries
            </p>
            <div className="ms-3">
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index + 1)}
                  className={`btn btn-sm rounded-circle me-1 pagination-button ${currentPage === index + 1
                    ? "paginationButton text-white"
                    : "btn-outline-secondary"
                    }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </Row>
      <Toaster type={toasterInfo.type}
        show={showToaster}
        setShow={setTosterShow}
        message={toasterInfo.message}
      />
    </>
  );
};

export default LogoRequestList;
